import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { App, Button, Tooltip } from 'antd';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import ContextBar from '../components/common/ContextBar';
import ResourceState from '../components/loading/ResourceState';
import { HOME_CRUMB } from '../utils/breadcrumbs';
import Guidance from '../components/common/Guidance';
import { confirmDestructive } from '../components/common/confirmDestructive';
import CombinedListSources from '../components/combinedLists/CombinedListSources';
import CombinedListNameModal from '../components/combinedLists/CombinedListNameModal';
import CombinedScopeSwitch, { SCOPE_COLLECTION, SCOPE_COMBINED } from '../components/combinedLists/CombinedScopeSwitch';
import EditSourcesModal from '../components/combinedLists/EditSourcesModal';
import { COMBINED_LIST_GUIDE } from '../components/combinedLists/CombinedListSuggestion';
import ItemFilterBuilder from '../components/items/ItemFilterBuilder';
import ItemFilterChips from '../components/items/ItemFilterChips';
import ItemsListBody from '../components/items/ItemsListBody';
import ItemsListToolbar from '../components/items/ItemsListToolbar';
import ListCount from '../components/items/ListCount';
import ListSearchBox from '../components/items/ListSearchBox';
import RefreshButton from '../components/common/RefreshButton';
import SortSelect from '../components/items/SortSelect';
import ViewModeSwitch from '../components/items/ViewModeSwitch';
import { KANBAN, PIN_ANGLE, SAVE_ICON } from '../components/icons';
import ItemsBoard from '../components/items/ItemsBoard';
import BoardControls from '../components/items/BoardControls';
import { boardElements, resolveBoard } from '../utils/board';
import { useOptionalCardDefinitions } from '../contexts/CardDefinitionsContext';
import { useOptionalFavorites } from '../contexts/FavoritesContext';
import useCombinedList from '../hooks/useCombinedList';
import { forgetCombinedList, refreshCombinedListMatches, rememberCombinedList } from '../hooks/useCombinedListMatches';
import useListSearch from '../hooks/useListSearch';
import { combineSortOptions } from '../hooks/useListSort';
import { createCombinedList, deleteCombinedList, updateCombinedList } from '../services/combinedLists';
import { PIN_KIND, setPin } from '../services/favorites';
import {
	cardPlural,
	combinedListPath,
	combinedSortOptions,
	decodeDefinition,
	newCombinedListHref,
	refusalWords,
	sourceListHref,
	vocabularyCard,
} from '../utils/combinedLists';
import { buildFilterFields } from '../utils/kqlBuilder';
import { normalizeId } from '../utils/ids';
import useWindowClass from '../hooks/useWindowClass';

export const COMBINED_NOT_FOUND_TITLE = 'List not found';
export const COMBINED_NOT_FOUND_TEXT = 'It may have been deleted, or it isn’t one of yours.';
const SEARCH_ID = 'combined-list-search';
const VIEW_MODES = ['grid', 'table', 'board'];
// Fields of ONE collection: its cards, its sub-collections, its element sets.
// A combined list's rows come from many, so they are not offered.
const LOCAL_FIELD_KEYS = new Set(['card', 'collection', 'set']);
const DEFAULT_SORT = { field: 'primaryDate', direction: 'desc' };
// The search term is the box's: its chip is hidden because the box shows it.
const SEARCH_PATHS = Object.freeze(['text']);

const sortOf = (field, direction) => (field ? { field, direction: direction === 'asc' ? 'asc' : 'desc' } : null);
const sameSort = (a, b) => (a?.field || DEFAULT_SORT.field) === (b?.field || DEFAULT_SORT.field)
	&& (a?.direction || DEFAULT_SORT.direction) === (b?.direction || DEFAULT_SORT.direction);

/**
 * THE COMBINED LIST — `/lists/:listId` (saved) and `/lists/new?def=…`
 * (unsaved) (KPE-034, KPR-1022; docs/plans/cross-collection-views.md §4 and
 * §6; docs/platform-standards.md §5 "A list across collections"). One card
 * across every collection the person lists — their own, the ones shared with
 * them, the global ones they follow — read as the List reads one collection:
 * the same search box, filter builder and chips, sort, grid and table.
 *
 *   header    "Combined list" over the list's name, on the matched card's
 *             tile. ONE filled primary: **Save list** (unsaved) or **Pin** /
 *             **Unpin** (saved). A saved list whose filter or sort was
 *             changed here gains **Save changes**, a labeled secondary.
 *             Behind ⋯: Rename…, Edit sources…, then Delete list (Danger).
 *   sources   the source strip (components/combinedLists/CombinedListSources):
 *             each collection's badge with its count, a door to that
 *             collection's own List under the same filter; a source read
 *             partly or missing an element says so quietly. A phone folds it
 *             into one button and a sheet.
 *   toolbar   search, filter, sort, the scope switch back to the collection
 *             the person came from (`from`), the count, Grid · Table.
 *   the list  the platform's tiles or table, `crossCollection`: every row's
 *             title is qualified by its collection and opens there.
 *
 * The filter and the sort live in the address. On a saved list an absent
 * `q` (or `sort`) means the stored one; a cleared filter is written `q=`.
 * Elements are named by the list's tokens (`#k7f3q2xa`), never one source's
 * ids — the vocabulary is read as one card's elements, so the List's own
 * builder writes them.
 */
export default function CombinedListPage() {
	const { listId: rawListId } = useParams();
	const listId = rawListId && rawListId !== 'new' ? rawListId : null;
	const navigate = useNavigate();
	const location = useLocation();
	const [params, setParams] = useSearchParams();
	const { message, modal } = App.useApp();
	const { compact: isMobile } = useWindowClass();
	const favorites = useOptionalFavorites();
	const cardDefinitions = useOptionalCardDefinitions();
	const cardsById = useMemo(() => cardDefinitions?.cardsById || {}, [cardDefinitions?.cardsById]);
	const getElementsForCard = cardDefinitions?.getElementsForCard;

	const defParam = params.get('def');
	const definition = useMemo(() => (listId ? null : decodeDefinition(defParam)), [listId, defParam]);
	const from = normalizeId(params.get('from')) || null;
	const viewAsked = VIEW_MODES.includes(params.get('view')) ? params.get('view') : 'grid';

	// The filter and sort live in the address. On a saved list an absent `q`
	// (or `sort`) means the stored one — the hook reads it (`data.query`).
	const hasSort = params.has('sort');
	const data = useCombinedList({
		listId,
		definition,
		q: params.has('q') ? params.get('q') || '' : null,
		sort: hasSort ? sortOf(params.get('sort'), params.get('dir')) : null,
	});
	const { list, sources, vocabulary, refusal, items, loading, loadingMore, error, hasMore, loadMore, reload, refresh, refreshing, refreshError, replaceItem, unknown, query, sort } = data;

	const update = useCallback((patch) => {
		setParams((current) => {
			const next = new URLSearchParams(current);
			Object.entries(patch).forEach(([key, value]) => {
				if (value === undefined) next.delete(key); else next.set(key, value);
			});
			return next;
		}, { replace: true });
	}, [setParams]);

	// A cleared filter is `q=`: on a saved list an absent `q` is the stored one.
	const setQuery = useCallback(next => update({ q: next ? next : '' }), [update]);
	const search = useListSearch({ query, setQuery });

	// The rows' own cards (a global collection's, a shared one's) — the tiles
	// and the table read their elements.
	useEffect(() => {
		if (!items.length || !cardDefinitions?.noteMissingCards) return;
		cardDefinitions.noteMissingCards(items.map(item => item.card_id).filter(Boolean));
	}, [items, cardDefinitions]);

	const referenceSource = sources.find(source => source?.card && source.status !== 'unreadable') || null;
	const referenceCard = referenceSource ? (cardsById[normalizeId(referenceSource.card._id)] || referenceSource.card) : null;
	const plural = referenceCard ? cardPlural(referenceCard) : 'Items';
	const title = list?.name || plural;

	// The vocabulary as one card: the List's filter builder, chips and sort read it unchanged.
	const vocabCard = useMemo(() => vocabularyCard(vocabulary, { name: plural }), [vocabulary, plural]);
	const filterFields = useMemo(
		() => buildFilterFields({ cards: [vocabCard] }).filter(field => !LOCAL_FIELD_KEYS.has(field.key)),
		[vocabCard]
	);
	const sortOptions = useMemo(() => combineSortOptions(combinedSortOptions(vocabulary)), [vocabulary]);

	// The board (KPR-1395): by an element of the list's vocabulary that a
	// board can be drawn by. Each row writes to its own card's element of
	// that name, in its own collection; the API decides who may.
	const boardOptions = useMemo(
		() => boardElements([vocabCard._id], () => vocabCard.elements),
		[vocabCard]
	);
	const board = useMemo(
		() => resolveBoard(boardOptions, { by: params.get('board'), lanes: params.get('lanes') }),
		[boardOptions, params]
	);
	// A board that cannot be drawn reads as the grid; while the vocabulary is
	// still on its way it stays a board, waiting.
	const view = viewAsked === 'board' && !board && !data.resolving && !loading ? 'grid' : viewAsked;
	const changeSort = useCallback((value) => {
		const at = value.lastIndexOf(':');
		update({ sort: value.slice(0, at), dir: value.slice(at + 1) });
	}, [update]);

	const total = useMemo(() => {
		const counted = sources.filter(source => typeof source?.count === 'number');
		if (!counted.length) return null;
		return counted.reduce((sum, source) => sum + source.count, 0);
	}, [sources]);

	const dirty = Boolean(list) && ((list.q || '') !== query || !sameSort(list.sort, sort));

	// ---- what the person does --------------------------------------------

	const [naming, setNaming] = useState(null); // 'save' | 'rename' | null
	const [nameError, setNameError] = useState(null);
	const [editing, setEditing] = useState(false);
	const [editError, setEditError] = useState(null);

	const pinned = Boolean(list && favorites?.isPinned?.(PIN_KIND.COMBINED_LIST, list._id));
	const togglePin = () => {
		if (!list || !favorites?.togglePin) return;
		favorites.togglePin(PIN_KIND.COMBINED_LIST, { targetId: list._id });
	};

	const saveNew = async ({ name, pin }) => {
		setNameError(null);
		try {
			const saved = await createCombinedList({ name, ...data.definition, q: query, sort: sameSort(sort, DEFAULT_SORT) ? null : sort });
			rememberCombinedList(saved);
			if (pin) {
				try {
					if (favorites?.togglePin) await favorites.togglePin(PIN_KIND.COMBINED_LIST, { targetId: saved._id }, { quiet: true });
					else await setPin(PIN_KIND.COMBINED_LIST, { targetId: saved._id }, true);
				} catch { /* the list is saved; the pin can be made from its header */ }
			}
			refreshCombinedListMatches();
			setNaming(null);
			message.success(pin ? `Saved “${name}” and pinned it to Home.` : `Saved “${name}”.`);
			navigate(combinedListPath(saved._id), { replace: true });
		} catch (err) {
			setNameError(refusalWords(err, 'The list could not be saved.'));
			throw err;
		}
	};

	const rename = async ({ name }) => {
		setNameError(null);
		try {
			const saved = await updateCombinedList(list._id, { name });
			data.setList(saved);
			rememberCombinedList(saved);
			setNaming(null);
			message.success('Renamed the list.');
		} catch (err) {
			setNameError(refusalWords(err, 'The list could not be renamed.'));
			throw err;
		}
	};

	const saveChanges = async () => {
		try {
			const saved = await updateCombinedList(list._id, { q: query || null, sort: sameSort(sort, DEFAULT_SORT) ? null : sort });
			data.setList(saved);
			rememberCombinedList(saved);
			update({ q: undefined, sort: undefined, dir: undefined });
			message.success('Saved the filter and sort.');
		} catch (err) {
			message.error(refusalWords(err, 'The changes could not be saved.'));
		}
	};

	const saveSources = async ({ exclude, include }) => {
		setEditError(null);
		if (!list) {
			setEditing(false);
			const href = newCombinedListHref({ ...data.definition, exclude, include }, { q: query, sort: hasSort ? sort.field : undefined, dir: hasSort ? sort.direction : undefined, view: view === 'grid' ? undefined : view, from: from || undefined });
			navigate(href, { replace: true });
			return;
		}
		try {
			const saved = await updateCombinedList(list._id, { exclude: exclude.length ? exclude : null, include: include.length ? include : null });
			data.setList(saved);
			rememberCombinedList(saved);
			setEditing(false);
			message.success('Updated the sources.');
		} catch (err) {
			setEditError(refusalWords(err, 'The sources could not be saved.'));
			throw err;
		}
	};

	const confirmDelete = () => {
		confirmDestructive(modal, {
			title: `Delete “${list?.name || 'this list'}”?`,
			content: 'The list goes, and so does its pin on Home. Nothing in any collection changes.',
			okText: 'Delete list',
			onOk: async () => {
				try {
					await deleteCombinedList(list._id);
					forgetCombinedList(list._id);
					refreshCombinedListMatches();
					message.success('Deleted the list.');
					navigate('/');
				} catch (err) {
					message.error(refusalWords(err, 'The list could not be deleted.'));
				}
			},
		});
	};

	// The way back to the collection the switch came from: its own List under
	// the filter it read (exact, in its ids), or its List as it was.
	const backSource = from ? sources.find(source => normalizeId(source?.collection?._id) === from) : null;
	const scopeSwitch = from ? (
		<CombinedScopeSwitch
			value={SCOPE_COMBINED}
			plural={plural}
			onChange={(scope) => {
				if (scope !== SCOPE_COLLECTION) return;
				navigate(sourceListHref(backSource) || `/collections/${from}/items`);
			}}
		/>
	) : null;

	// ---- the page --------------------------------------------------------

	if (data.notFound) {
		return (
			<div className="combined-list combined-list--missing">
				<ContextBar title={COMBINED_NOT_FOUND_TITLE} breadcrumb={[HOME_CRUMB]} />
				<ResourceState state="not-found" body={COMBINED_NOT_FOUND_TEXT} backTo="/" backLabel="Go to Home" />
			</div>
		);
	}

	const primary = list ? (
		<Tooltip title={isMobile ? (pinned ? 'Unpin from Home' : 'Pin to Home') : undefined}>
			<Button
				type={pinned ? 'default' : 'primary'}
				icon={<PIN_ANGLE />}
				aria-pressed={pinned}
				aria-label={isMobile ? (pinned ? 'Unpin from Home' : 'Pin to Home') : undefined}
				onClick={togglePin}
				data-testid="combined-list-pin-button"
			>
				{isMobile ? null : (pinned ? 'Unpin' : 'Pin')}
			</Button>
		</Tooltip>
	) : (
		<Tooltip title={isMobile ? 'Save list' : undefined}>
			<Button
				type="primary"
				icon={<SAVE_ICON />}
				aria-label={isMobile ? 'Save list' : undefined}
				onClick={() => { setNameError(null); setNaming('save'); }}
				disabled={!data.definition || Boolean(refusal)}
				data-testid="combined-list-save"
			>
				{isMobile ? null : 'Save list'}
			</Button>
		</Tooltip>
	);
	const actions = (
		<>
			{dirty ? <Button onClick={saveChanges} data-testid="combined-list-save-changes">Save changes</Button> : null}
			{primary}
		</>
	);
	const menuActions = [
		list ? { key: 'rename', group: 'record', glyph: 'edit', label: 'Rename…', onClick: () => { setNameError(null); setNaming('rename'); } } : null,
		{ key: 'sources', group: 'record', glyph: 'filter', label: 'Edit sources…', onClick: () => { setEditError(null); setEditing(true); } },
		list ? { key: 'delete', group: 'destructive', glyph: 'delete', label: 'Delete list…', onClick: confirmDelete } : null,
	].filter(Boolean);

	const strip = sources.length || !data.resolving ? (
		<CombinedListSources sources={sources} isMobile={isMobile} onEditSources={() => setEditing(true)} />
	) : null;

	const searchBox = <ListSearchBox search={search} id={SEARCH_ID} label={`Search ${plural}`} placeholder={`Search ${plural}`} />;
	const filterBuilder = <ItemFilterBuilder fields={filterFields} query={query} onApply={setQuery} />;
	const filterChips = query ? (
		<ItemFilterChips query={query} fields={filterFields} onChange={setQuery} className="items-page__filter-chips" hiddenPaths={SEARCH_PATHS} />
	) : null;
	const sortSelect = <SortSelect field={sort?.field || DEFAULT_SORT.field} direction={sort?.direction || DEFAULT_SORT.direction} options={sortOptions} onChange={changeSort} />;
	const countLabel = <ListCount count={total} loading={loading} failed={Boolean(error)} />;
	// Refresh (KPR-1346): beside the count, a square glyph (standards §3).
	const refreshButton = <RefreshButton pending={refreshing} onRefresh={refresh} disabled={loading} />;
	const viewSwitch = (
		<ViewModeSwitch
			value={view}
			onChange={next => update({ view: next === 'grid' ? undefined : next })}
			isMobile={isMobile}
			extras={boardOptions.length || view === 'board' ? [{
				value: 'board',
				label: 'Board',
				title: 'Board',
				mobileIcon: <KANBAN role="img" aria-label="Board" />,
			}] : []}
		/>
	);
	const boardControls = view === 'board' && board ? (
		<BoardControls
			options={boardOptions}
			by={board.by.name}
			lanes={board.lanes?.name || null}
			onChange={({ by, lanes }) => update({ board: by || undefined, lanes: lanes || undefined })}
			isMobile={isMobile}
		/>
	) : null;

	const toolbar = (
		<ItemsListToolbar
			stacked={isMobile}
			search={searchBox}
			primary={isMobile ? <>{scopeSwitch}{viewSwitch}</> : null}
			secondary={isMobile ? <>{filterBuilder}{sortSelect}{countLabel}{refreshButton}</> : null}
			rows={isMobile && boardControls ? [boardControls] : []}
			chips={isMobile && filterChips ? filterChips : null}
		>
			{isMobile ? null : (
				<>
					{filterChips}
					{filterBuilder}
					{scopeSwitch}
					<span className="items-page__toolbar-divider" aria-hidden="true" />
					{sortSelect}
					{boardControls}
					<span className="items-page__toolbar-spacer" />
					{countLabel}
					{refreshButton}
					{viewSwitch}
				</>
			)}
		</ItemsListToolbar>
	);

	const dropped = Array.isArray(location.state?.droppedConditions) ? location.state.droppedConditions : [];
	const unknownNames = (unknown || []).map(entry => entry?.name).filter(Boolean);

	const emptyNode = (
		<p className="combined-list__empty">
			{sources.length
				? (query ? `No ${plural.toLowerCase()} match this filter. Clear it to see them all.` : `None of these collections has ${plural.toLowerCase()} yet.`)
				: `None of your collections holds ${plural.toLowerCase()} now. Follow or add one that does, and it joins this list by itself.`}
		</p>
	);

	return (
		<div className="combined-list">
			<ContextBar
				breadcrumb={[HOME_CRUMB]}
				kicker="Combined list"
				record={referenceCard || undefined}
				kind="card"
				title={title}
				below={strip}
				actions={actions}
				menuActions={menuActions}
			>
				{refusal ? null : toolbar}
			</ContextBar>

			{dropped.length ? (
				<Guidance
					className="combined-list__note"
					tone="info"
					title="Some of your filter stayed in the collection"
					why={`Not every collection here has ${dropped.join(', ')}, so ${dropped.length === 1 ? 'that condition was' : 'those conditions were'} left out.`}
					links={[COMBINED_LIST_GUIDE]}
				/>
			) : null}
			{unknownNames.length ? (
				<Guidance
					className="combined-list__note"
					tone="info"
					title="The filter names something none of these collections has"
					why={`${unknownNames.join(', ')} matches nothing here. Take it out of the filter to see more.`}
				/>
			) : null}

			{refusal ? (
				<div className="combined-list__refusal" role="status">
					<p>{refusalWords({ response: { data: refusal } })}</p>
					{refusal.code === 'too_many_sources' ? (
						<Button type="link" onClick={() => setEditing(true)}>Edit sources</Button>
					) : null}
				</div>
			) : (
				<section className="combined-list__list" aria-label={plural}>
					{view === 'board' ? (
						<ItemsBoard
							source={{
								items,
								total: total ?? items.length,
								loading,
								loadingMore,
								error: error ? { kind: 'failed' } : null,
								retry: reload,
								hasMore,
								loadMore,
							}}
							by={board?.by || null}
							lanes={board?.lanes || null}
							cardsById={cardsById}
							getElementsForCard={getElementsForCard}
							onItemUpdated={replaceItem}
							onItemRemoved={refresh}
							isMobile={isMobile}
							crossCollection
							emptyText={`No ${plural.toLowerCase()} match`}
						/>
					) : (
					<ItemsListBody
						view={view}
						items={items}
						// A Refresh keeps the rows under the quiet bar, and one
						// that fails says so above them with Retry (KPR-1346).
						loading={loading || refreshing}
						error={error || (refreshError ? 'Showing what was loaded before.' : null)}
						onRetry={error ? reload : refresh}
						empty={emptyNode}
						label={plural.toLowerCase()}
						cardsById={cardsById}
						getElementsForCard={getElementsForCard}
						onItemUpdated={replaceItem}
						crossCollection
					/>
					)}
					{hasMore && view !== 'board' ? (
						<div className="combined-list__more">
							<Button onClick={loadMore} loading={loadingMore}>Show more</Button>
						</div>
					) : null}
				</section>
			)}

			<CombinedListNameModal
				open={naming !== null}
				mode={naming || 'save'}
				initialName={naming === 'rename' ? (list?.name || '') : plural}
				error={nameError}
				onSubmit={naming === 'rename' ? rename : saveNew}
				onCancel={() => setNaming(null)}
			/>
			<EditSourcesModal
				open={editing}
				isMobile={isMobile}
				sources={refusal?.code === 'too_many_sources'
					? (refusal.sources || []).map(pair => ({ collection: { _id: pair.collection }, card: { _id: pair.card }, status: 'ok' }))
					: sources}
				definition={data.definition}
				error={editError}
				onSubmit={saveSources}
				onCancel={() => setEditing(false)}
			/>
		</div>
	);
}
